import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BatteryCharging,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { useCaseCommand } from "hooks/useCaseDashboard";
import { isRefusedCommand } from "utils/caseDashboard";
import {
  BATTERY_LOG_CASE_MIN,
  batteryLogCommand,
  readingsFromCase,
} from "utils/batteryLog";
import BatteryHistoryChart from "../common/BatteryHistoryChart";
import {
  Card,
  EmptyState,
  Notice,
  SecondaryButton,
  fmtDateTime,
  fmtShortMac,
} from "./parts";

// Each wearable's battery history, as the case last pulled it from the
// wearable (case software v0.11.0+: `battery-log`, `battery-log <MAC>`).
// An older case refuses the command; the view says which version it needs.

const fmtEpoch = (s) => (s ? fmtDateTime(new Date(s * 1000)) : null);

/** A command's error, or null: transport errors and `ok: false`. */
const commandError = (query) =>
  query.data?.ok === false
    ? query.data.error || "error"
    : !query.data && query.error
    ? query.error
    : null;

const BatteryView = ({ caseId }) => {
  const { t } = useTranslation();
  const list = useCaseCommand(caseId, "battery-log", 0);
  const [selected, setSelected] = useState(null);
  const refused = isRefusedCommand(list.error);
  const error = commandError(list);

  if (selected) {
    return (
      <BatteryDetail
        caseId={caseId}
        wearable={selected}
        onBack={() => setSelected(null)}
      />
    );
  }

  const wearables = (list.data?.ok === false ? [] : list.data?.wearables) || [];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-[#111827]">
            {t("caseDashboard.tabs.battery")}
          </h1>
          <p className="mt-0.5 text-[13px] text-[#6B7280]">
            {t("caseDashboard.battery.subtitle")}
          </p>
        </div>
        {!refused && (
          <SecondaryButton onClick={list.refresh}>
            <RefreshCw size={14} />
            {t("caseDashboard.battery.refresh")}
          </SecondaryButton>
        )}
      </div>

      {refused ? (
        <Card>
          <EmptyState
            icon={BatteryCharging}
            title={t("caseDashboard.battery.needsTitle")}
            body={t("caseDashboard.common.needsVersion", {
              version: BATTERY_LOG_CASE_MIN,
            })}
          />
        </Card>
      ) : (
        <Card title={t("caseDashboard.battery.listTitle")}>
          {list.loading && !list.data ? (
            <EmptyState
              icon={BatteryCharging}
              title={t("caseDashboard.common.loading")}
            />
          ) : error ? (
            <div className="p-4">
              <Notice tone="error">
                {t("caseDashboard.common.queryError", { message: error })}
              </Notice>
            </div>
          ) : wearables.length === 0 ? (
            <EmptyState
              icon={BatteryCharging}
              title={t("caseDashboard.battery.noneTitle")}
              body={t("caseDashboard.battery.noneBody")}
            />
          ) : (
            <ul>
              {wearables.map((w) => {
                const first = fmtEpoch(w.first_epoch);
                const last = fmtEpoch(w.last_epoch);
                return (
                  <li key={w.mac}>
                    <button
                      type="button"
                      onClick={() => setSelected(w)}
                      className="flex w-full items-center gap-3 border-b border-[#F3F4F6] px-4 py-3 text-left transition duration-150 last:border-b-0 hover:bg-[#FAFAFA]"
                    >
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
                        <BatteryCharging size={15} className="text-[#6B7280]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-[#111827]">
                          {w.name || `SIT-${fmtShortMac(w.mac)}`}
                        </p>
                        <p className="truncate font-mono text-[11px] text-[#9CA3AF]">
                          {w.mac}
                        </p>
                      </div>
                      <div className="hidden text-right sm:block">
                        <p className="text-[12px] text-[#374151]">
                          {t("caseDashboard.battery.samples", {
                            count: Number(w.samples) || 0,
                          })}
                        </p>
                        <p className="text-[11px] text-[#9CA3AF]">
                          {first && last
                            ? t("caseDashboard.battery.span", { first, last })
                            : t("caseDashboard.battery.clockUnknown")}
                        </p>
                      </div>
                      <div className="hidden w-40 text-right md:block">
                        <p className="text-[11px] text-[#9CA3AF]">
                          {t("caseDashboard.battery.pulled", {
                            time: fmtDateTime(w.pulled_at),
                          })}
                        </p>
                      </div>
                      <ChevronRight
                        size={15}
                        className="flex-shrink-0 text-[#D1D5DB]"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
};

const BatteryDetail = ({ caseId, wearable, onBack }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true });
  const [nonce, setNonce] = useState(0);
  const command = batteryLogCommand(wearable.mac);

  useEffect(() => {
    let active = true;
    if (!command) {
      setState({ loading: false, error: "bad mac" });
      return undefined;
    }
    setState({ loading: true });
    window.electronAPI?.caseDashboard
      ?.run(caseId, command)
      .then((res) => {
        if (!active) return;
        const json = res?.json;
        if (json && json.ok !== false) {
          setState({ loading: false, json });
        } else {
          const message =
            json?.error || res?.error?.message || res?.error || "error";
          setState({
            loading: false,
            error: isRefusedCommand(message)
              ? t("caseDashboard.common.needsVersion", {
                  version: BATTERY_LOG_CASE_MIN,
                })
              : /no battery log/i.test(message)
              ? t("caseDashboard.battery.noLog")
              : t("caseDashboard.common.queryError", { message }),
          });
        }
      })
      .catch((err) => {
        if (active)
          setState({
            loading: false,
            error: t("caseDashboard.common.queryError", {
              message: err?.message || String(err),
            }),
          });
      });
    return () => {
      active = false;
    };
  }, [caseId, command, nonce, t]);

  const json = state.json;
  const readings = readingsFromCase(json);
  const name =
    json?.name || wearable.name || `SIT-${fmtShortMac(wearable.mac)}`;

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-xs font-medium text-[#6B7280] transition-colors duration-150 hover:text-[#E07A3A]"
      >
        <ChevronLeft size={14} />
        {t("caseDashboard.battery.back")}
      </button>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-[#111827]">{name}</h1>
          <p className="mt-0.5 font-mono text-[12px] text-[#9CA3AF]">
            {wearable.mac}
            {json?.pulled_at
              ? ` · ${t("caseDashboard.battery.pulled", {
                  time: fmtDateTime(json.pulled_at),
                })}`
              : ""}
          </p>
        </div>
        <SecondaryButton
          onClick={() => setNonce((n) => n + 1)}
          disabled={state.loading}
        >
          <RefreshCw
            size={14}
            className={state.loading ? "animate-spin" : ""}
          />
          {t("caseDashboard.battery.refresh")}
        </SecondaryButton>
      </div>
      <Card title={t("caseDashboard.battery.chartTitle")}>
        <div className="p-4">
          {state.error ? (
            <Notice tone="error">{state.error}</Notice>
          ) : (
            <BatteryHistoryChart
              readings={readings}
              intervalS={Number(json?.interval_s) || 60}
              loading={state.loading}
              height={300}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default BatteryView;
